import { useState, useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';

/**
 * Custom hook for managing standard filter state and operations
 * 
 * @param {Object} options
 * @param {Object} options.initialFilters - Initial filter values
 * @param {Function} options.onFilterChange - Callback when filters change
 * @param {Function} options.onApplyFilters - Callback when apply filters is clicked
 * @param {number} options.debounceDelay - Debounce delay for search filters (default: 300ms)
 * @param {boolean} options.autoApply - Whether to auto-apply filters on change (default: false)
 * @returns {Object} Filter management utilities
 */
export const useStandardFilter = ({
  initialFilters = {},
  onFilterChange,
  onApplyFilters,
  debounceDelay = 300,
  autoApply = false
} = {}) => {
  const [filters, setFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(false);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  // Calculate active filters count
  const calculateActiveFilters = useCallback((filterValues) => {
    return Object.values(filterValues).filter(value => 
      value !== null && 
      value !== undefined && 
      value !== '' && 
      !(Array.isArray(value) && value.length === 0)
    ).length;
  }, []);

  // Debounced apply filters function (must be defined before handleFilterChange)
  const debouncedApplyFilters = useMemo(
    () => debounce((filterValues) => {
      if (onApplyFilters) {
        setLoading(true);
        onApplyFilters(filterValues).finally(() => {
          setLoading(false);
        });
      }
    }, debounceDelay),
    [onApplyFilters, debounceDelay]
  );

  // Handle filter changes
  const handleFilterChange = useCallback((changedValues, allValues) => {
    const newFilters = { ...filters, ...changedValues };
    setFilters(newFilters);
    setActiveFiltersCount(calculateActiveFilters(newFilters));
    
    if (onFilterChange) {
      onFilterChange(changedValues, newFilters);
    }

    // Auto-apply if enabled
    if (autoApply) {
      debouncedApplyFilters(newFilters);
    }
  }, [filters, onFilterChange, calculateActiveFilters, autoApply, debouncedApplyFilters]);

  // Handle apply filters
  const handleApplyFilters = useCallback((filterValues = filters) => {
    if (onApplyFilters) {
      setLoading(true);
      onApplyFilters(filterValues).finally(() => {
        setLoading(false);
      });
    }
  }, [filters, onApplyFilters]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    setFilters({});
    setActiveFiltersCount(0);
    
    if (onApplyFilters) {
      setLoading(true);
      onApplyFilters({}).finally(() => {
        setLoading(false);
      });
    }
  }, [onApplyFilters]);

  // Update filters externally
  const updateFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    setActiveFiltersCount(calculateActiveFilters(newFilters));
  }, [calculateActiveFilters]);

  // Reset filters to initial values
  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
    setActiveFiltersCount(calculateActiveFilters(initialFilters));
  }, [initialFilters, calculateActiveFilters]);

  // Get formatted filters for API calls
  const getFormattedFilters = useCallback((filterValues = filters) => {
    const formatted = {};
    
    Object.entries(filterValues).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value) && value.length === 0) {
          return; // Skip empty arrays
        }
        
        // Format dates
        if (value && typeof value === 'object' && value.format) {
          formatted[key] = value.format('YYYY-MM-DD');
        }
        // Format date ranges
        else if (Array.isArray(value) && value.length === 2 && value[0] && value[1]) {
          formatted[`${key}From`] = value[0].format('YYYY-MM-DD');
          formatted[`${key}To`] = value[1].format('YYYY-MM-DD');
        }
        // Format other values
        else {
          formatted[key] = value;
        }
      }
    });
    
    return formatted;
  }, [filters]);

  return {
    // State
    filters,
    loading,
    activeFiltersCount,
    
    // Actions
    handleFilterChange,
    handleApplyFilters,
    handleClearFilters,
    updateFilters,
    resetFilters,
    
    // Utilities
    getFormattedFilters,
    calculateActiveFilters
  };
};

export default useStandardFilter;






